import { TPromoCodeAttribute, TPromoCodeRequest } from "../../shared/promo-types";
import { BeamPromoCodesStoredEvent } from "../../utils/events";
import {
  formatStoreAndReturnPromoCodes,
  getPromoCodeFromShopifyCookie,
  getPromoCodesLocalStorage,
  getSanitizedUrlForStorage,
  promoCodesAreEqual,
} from "../../utils/promoManager";
import { BeamConfigOptions, BeamPlugin } from "../beam";
import { logger } from "../utils";

type PromoManagerPluginOptions = {
  disableDiscountCodeCookieCheck?: boolean;
};

/**
 * Manages promo code lifecycle including:
 * - URL sanitization
 * - Storage management
 * - Event emission
 *
 * @implements {BeamPlugin}
 */
export class PromoManagerPlugin implements BeamPlugin {
  name = "promoManager";
  /**
   * When true, disables Shopify discount cookie checking.
   * Set this to true for non-Shopify integrations.
   */
  disableDiscountCodeCookieCheck: boolean;

  private promoCodes: TPromoCodeRequest = {
    unvalidatedPromoCodes: [],
    validatedPromoCodes: [],
  };

  constructor(options: PromoManagerPluginOptions) {
    this.disableDiscountCodeCookieCheck = !!options?.disableDiscountCodeCookieCheck;
  }

  /**
   * Initializes promo code management:
   * - Extracts and sanitizes promo codes from URL
   * - Stores formatted codes
   * - Emits storage event
   *
   * @param {BeamConfigOptions} config - Plugin configuration
   */

  async init(config: BeamConfigOptions) {
    try {
      const sanitizedUrl = this.#getSanitizedUrl();
      const cookieCode = this.disableDiscountCodeCookieCheck ? null : this.#getPromoCodeFromShopifyCookie();

      if (sanitizedUrl || cookieCode) {
        const previouslyStoredPromoCodes = getPromoCodesLocalStorage({ apiKey: config.apiKey }) ?? {
          unvalidatedPromoCodes: [],
          validatedPromoCodes: [],
        };
        const sanitizedPromoCodes: TPromoCodeAttribute[] = [];
        if (sanitizedUrl) sanitizedPromoCodes.push(sanitizedUrl);
        if (cookieCode) sanitizedPromoCodes.push({ value: cookieCode });

        this.promoCodes = formatStoreAndReturnPromoCodes(sanitizedPromoCodes, config.apiKey);
        const hasChanged = !promoCodesAreEqual(this.promoCodes, previouslyStoredPromoCodes);

        if (hasChanged) {
          this.#emitPromoCodeStoredEvent();
        }
      }
    } catch (error) {
      logger.error("[PromoManager] Initialization failed:", error);
    }
  }

  /**
   * Retrieves and sanitizes current URL for promo code extraction
   * @private
   * @returns {TPromoCodeAttribute} Sanitized URL attributes
   */
  #getSanitizedUrl(): TPromoCodeAttribute | undefined {
    try {
      const sanitizedUrl = getSanitizedUrlForStorage();
      return sanitizedUrl;
    } catch (error) {
      logger.error("[PromoManager] URL sanitization error:", error);
      return undefined;
    }
  }

  #getPromoCodeFromShopifyCookie(): string | null {
    try {
      return getPromoCodeFromShopifyCookie();
    } catch (error) {
      logger.error("[PromoManager] Cookie extraction error:", error);
      return null;
    }
  }

  /**
   * Emits event when promo codes are stored
   * @private
   */
  #emitPromoCodeStoredEvent() {
    if (!this.promoCodes) {
      return;
    }

    try {
      const event = new BeamPromoCodesStoredEvent({
        promoCodes: { ...this.promoCodes },
      });
      window.dispatchEvent(event);
    } catch (error) {
      logger.error("[PromoManager] Failed to dispatch event:", error);
    }
  }
}
